import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Check, X, PencilSimple } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useEditConfirm } from "../../context/EditConfirmContext";

function Countdown({ scheduledSendAt }) {
  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    const tick = () => {
      const diffMs = new Date(scheduledSendAt).getTime() - Date.now();
      if (diffMs <= 0) return setRemaining("sending now...");
      const mins = Math.floor(diffMs / 60000);
      const secs = Math.floor((diffMs % 60000) / 1000);
      setRemaining(`${mins}:${secs.toString().padStart(2, "0")}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [scheduledSendAt]);
  return <span data-testid="pending-reply-countdown">{remaining}</span>;
}

export const PendingRepliesPanel = ({ onChanged }) => {
  const confirmEdit = useEditConfirm();
  const [pending, setPending] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draftBody, setDraftBody] = useState("");

  const fetchPending = async () => {
    const { data } = await api.get("/admin/pending-replies");
    setPending(data);
  };

  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 15000);
    return () => clearInterval(interval);
  }, []);

  const sendNow = async (id) => {
    try {
      await api.post(`/admin/pending-replies/${id}/send-now`);
      toast.success("Reply sent");
      fetchPending();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const cancelReply = async (id) => {
    try {
      await api.post(`/admin/pending-replies/${id}/cancel`);
      toast.success("Draft cancelled");
      fetchPending();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveEdit = async (id) => {
    if (!(await confirmEdit("this draft reply"))) return;
    try {
      await api.patch(`/admin/pending-replies/${id}`, { body: draftBody });
      toast.success("Draft updated");
      setEditingId(null);
      fetchPending();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (pending.length === 0) return null;

  return (
    <div className="bg-igc-surface border border-igc-gold/20 p-5 mb-6" data-testid="pending-replies-panel">
      <p className="text-igc-cream font-serif text-lg mb-1 flex items-center gap-2">
        <Clock size={18} weight="duotone" className="text-igc-goldText" /> Pending Nate Replies ({pending.length})
      </p>
      <p className="text-igc-muted text-xs mb-3">Nate drafted these replies. Review, edit, or cancel within the grace window — otherwise they auto-send.</p>
      <div className="space-y-3">
        {pending.map((p) => (
          <div key={p.id} className="bg-igc-surfaceLight p-4" data-testid={`pending-reply-row-${p.id}`}>
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <p className="text-igc-cream text-sm">{p.email} <span className="text-igc-muted text-xs">({p.lead_source})</span></p>
              <span className="text-igc-goldText text-xs flex items-center gap-1" data-testid={`pending-reply-timer-${p.id}`}>
                <Clock size={12} weight="bold" /> Auto-sends in <Countdown scheduledSendAt={p.scheduled_send_at} />
              </span>
            </div>
            {editingId === p.id ? (
              <div className="space-y-2">
                <Textarea data-testid={`pending-reply-edit-textarea-${p.id}`} value={draftBody} onChange={(e) => setDraftBody(e.target.value)} title="Edit the text of Nate's drafted reply before it auto-sends." className="bg-igc-surface border-igc-gold/20 text-igc-cream text-xs min-h-[120px]" />
                <div className="flex gap-2">
                  <Button data-testid={`pending-reply-save-edit-${p.id}`} onClick={() => saveEdit(p.id)} title="Saves your edited version of this draft reply." className="bg-igc-gold hover:bg-igc-goldHover text-black text-xs h-8">Save Draft</Button>
                  <Button data-testid={`pending-reply-cancel-edit-${p.id}`} onClick={() => setEditingId(null)} variant="outline" title="Discards your edits without saving." className="border-igc-gold/30 text-igc-muted text-xs h-8">Cancel Edit</Button>
                </div>
              </div>
            ) : (
              <p className="text-igc-muted text-xs whitespace-pre-wrap mb-3" data-testid={`pending-reply-body-${p.id}`}>{p.body}</p>
            )}
            {editingId !== p.id && (
              <div className="flex gap-2">
                <Button data-testid={`pending-reply-send-now-${p.id}`} onClick={() => sendNow(p.id)} title="Sends this reply to the lead immediately, instead of waiting for the countdown." className="bg-igc-gold hover:bg-igc-goldHover text-black text-xs h-8 flex items-center gap-1">
                  <Check size={12} weight="bold" /> Send Now
                </Button>
                <Button data-testid={`pending-reply-edit-${p.id}`} onClick={() => { setEditingId(p.id); setDraftBody(p.body); }} variant="outline" title="Lets you edit the wording of this draft before it sends." className="border-igc-gold/30 text-igc-goldText text-xs h-8 flex items-center gap-1">
                  <PencilSimple size={12} weight="bold" /> Edit
                </Button>
                <Button data-testid={`pending-reply-cancel-${p.id}`} onClick={() => cancelReply(p.id)} variant="outline" title="Cancels this draft — it will never be sent to the lead." className="border-igc-danger/30 text-igc-danger text-xs h-8 flex items-center gap-1">
                  <X size={12} weight="bold" /> Cancel
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
